import {
  UNDOABLE_GAME_ACTION_TYPES,
  isGameActionType,
  type GameActionType,
} from "./actions.js";

declare const structuredClone: <Value>(value: Value) => Value;

export type GameSide = "us" | "them";
export type GameActionErrorCode = "invalid-argument" | "failed-precondition";
export type BaseIndex = 0 | 1 | 2;
export type RunnerDestination = number | "H" | "X";

export interface BaseballStats {
  AB: number;
  H: number;
  "1B": number;
  "2B": number;
  "3B": number;
  HR: number;
  BB: number;
  K: number;
  RBI: number;
  R: number;
  SF: number;
}

export interface PlateAppearanceResult {
  key: string;
  label: string;
  bases?: 1 | 2 | 3 | 4;
  hit?: boolean;
  walk?: boolean;
  out?: boolean;
  fc?: boolean;
  error?: boolean;
  sf?: boolean;
}

export const RESULTS = {
  "1B": { key: "1B", label: "1B", hit: true, bases: 1 },
  "2B": { key: "2B", label: "2B", hit: true, bases: 2 },
  "3B": { key: "3B", label: "3B", hit: true, bases: 3 },
  HR: { key: "HR", label: "HR", hit: true, bases: 4 },
  BB: { key: "BB", label: "BB", walk: true, bases: 1 },
  K: { key: "K", label: "K", out: true },
  GO: { key: "GO", label: "GO", out: true },
  FO: { key: "FO", label: "FO", out: true },
  FC: { key: "FC", label: "FC", fc: true, bases: 1 },
  E: { key: "E", label: "E", error: true, bases: 1 },
  SF: { key: "SF", label: "SF", out: true, sf: true },
} as const satisfies Record<string, PlateAppearanceResult>;

export type GameResultKey = keyof typeof RESULTS;

export const NUM_REGULATION_INNINGS = 9;
export const DEFAULT_RUN_CAP = 6;
export const MERCY_RULE_RUN_DIFFERENTIAL = 15;
export const MERCY_RULE_FIRST_INNING = 7;
export const MERCY_RULE_WARNING_INNING = 6;

export const FORFEIT_WINNER_VALUES = ["us", "them"] as const;
export const FORFEIT_WINNERS: ReadonlySet<GameSide> = new Set(
  FORFEIT_WINNER_VALUES,
);

const HIT_RESULT_KEYS: ReadonlySet<keyof BaseballStats> = new Set([
  "1B",
  "2B",
  "3B",
  "HR",
]);

const UNDOABLE: ReadonlySet<GameActionType> = new Set(
  UNDOABLE_GAME_ACTION_TYPES,
);

export interface GameLineupEntry {
  playerId: string;
  name: string;
  number?: number | null;
}

export interface GameState {
  inning: number;
  batting: GameSide;
  outs: number;
  batterIndex: number;
  bases: [string | null, string | null, string | null];
  ourRunsByInning: number[];
  opponentRunsByInning: number[];
  statsByPlayerId: Record<string, BaseballStats>;
  lastBatterId: string | null;
  final: boolean;
  mercyRuleWinner?: GameSide;
}

export interface GameDomainDocument {
  state: GameState;
  isHome: boolean;
  openInning: boolean;
  lineup: GameLineupEntry[];
  opponentName: string;
  defense: unknown[];
}

export interface GameTeamContext {
  jerseyHolderPlayerId?: string | null;
}

export interface GameAction {
  type?: unknown;
  payload?: unknown;
}

export interface GameAudit {
  type: GameActionType;
  summary: string;
  payload: Record<string, unknown>;
}

export interface GameTeamEffects {
  jerseyHolderPlayerId?: string | null;
}

export interface GameInversePatch {
  state: GameState;
  openInning: boolean;
  jerseyHolderPlayerId: string | null;
  crossedHalfInning: boolean;
}

interface DomainActionResult {
  teamEffects: GameTeamEffects;
  audit: GameAudit;
}

export interface GameActionResult<Game extends GameDomainDocument> {
  nextGame: Game;
  teamEffects: GameTeamEffects;
  audit: GameAudit;
  inversePatch: GameInversePatch | null;
  pushUndo: boolean;
}

export class GameActionError extends Error {
  code: GameActionErrorCode;
  details?: Record<string, unknown>;

  constructor(
    code: GameActionErrorCode,
    message: string,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "GameActionError";
    this.code = code;
    if (details !== undefined) this.details = details;
  }
}

export function emptyStats(): BaseballStats {
  return {
    AB: 0,
    H: 0,
    "1B": 0,
    "2B": 0,
    "3B": 0,
    HR: 0,
    BB: 0,
    K: 0,
    RBI: 0,
    R: 0,
    SF: 0,
  };
}

export function createInitialGameState(isHome: boolean): GameState {
  return {
    inning: 1,
    batting: isHome ? "them" : "us",
    outs: 0,
    batterIndex: 0,
    bases: [null, null, null],
    ourRunsByInning: [0],
    opponentRunsByInning: [0],
    statsByPlayerId: {},
    lastBatterId: null,
    final: false,
  };
}

export function totalRuns(
  runsByInning: readonly number[] | null | undefined,
): number {
  return (runsByInning ?? []).reduce((sum, runs) => sum + runs, 0);
}

export interface InningRulesGame {
  openInning: boolean;
  state: Pick<GameState, "inning">;
}

export interface RunCapGame {
  openInning: boolean;
  state: Pick<GameState, "inning" | "ourRunsByInning" | "opponentRunsByInning">;
}

export function isOpenInning(game: InningRulesGame): boolean {
  return (
    game.state.inning >= NUM_REGULATION_INNINGS || Boolean(game.openInning)
  );
}

export function runsCapFor(game: RunCapGame, forUs: boolean): number {
  if (isOpenInning(game)) return Infinity;
  const index = game.state.inning - 1;
  const oursEntering =
    totalRuns(game.state.ourRunsByInning) -
    (game.state.ourRunsByInning[index] ?? 0);
  const theirsEntering =
    totalRuns(game.state.opponentRunsByInning) -
    (game.state.opponentRunsByInning[index] ?? 0);
  const deficit = forUs
    ? theirsEntering - oursEntering
    : oursEntering - theirsEntering;
  return deficit > DEFAULT_RUN_CAP ? deficit + 1 : DEFAULT_RUN_CAP;
}

export interface MercyRuleOpportunity {
  side: GameSide;
  runsRemaining: number;
  runsNeededToPrevent: number | null;
}

export function mercyRuleOpportunity(
  game: GameDomainDocument | null | undefined,
): MercyRuleOpportunity | null {
  const state = game?.state;
  if (
    !game ||
    !state ||
    state.final ||
    state.inning < MERCY_RULE_WARNING_INNING
  ) {
    return null;
  }
  const ourRuns = totalRuns(state.ourRunsByInning);
  const opponentRuns = totalRuns(state.opponentRunsByInning);
  const differential = ourRuns - opponentRuns;
  const absoluteDifferential = Math.abs(differential);

  if (absoluteDifferential >= MERCY_RULE_RUN_DIFFERENTIAL) {
    return {
      side: differential > 0 ? "us" : "them",
      runsRemaining: 0,
      runsNeededToPrevent:
        absoluteDifferential - MERCY_RULE_RUN_DIFFERENTIAL + 1,
    };
  }

  const side = state.batting === "them" ? "them" : "us";
  const battingDifferential = side === "us" ? differential : -differential;
  const runsRemaining = MERCY_RULE_RUN_DIFFERENTIAL - battingDifferential;
  const runsByInning =
    side === "us" ? state.ourRunsByInning : state.opponentRunsByInning;
  const inningRuns = runsByInning[state.inning - 1] ?? 0;
  const runCap = runsCapFor(game, side === "us");
  const scoringCapacity = Number.isFinite(runCap)
    ? Math.max(0, runCap - inningRuns)
    : Infinity;

  if (runsRemaining > scoringCapacity) return null;
  return { side, runsRemaining, runsNeededToPrevent: null };
}

function clone<Value>(value: Value): Value {
  return structuredClone(value);
}

function recordValue(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object"
    ? (value as Record<string, unknown>)
    : {};
}

function ensureStats(state: GameState, playerId: string): BaseballStats {
  state.statsByPlayerId[playerId] ??= emptyStats();
  return state.statsByPlayerId[playerId];
}

function bump(
  state: GameState,
  playerId: string,
  key: keyof BaseballStats,
  amount = 1,
): void {
  const stats = ensureStats(state, playerId);
  stats[key] += amount;
}

function ensureInning(runs: number[], index: number): number[] {
  while (runs.length <= index) runs.push(0);
  return runs;
}

function playerName(game: GameDomainDocument, playerId: string): string {
  return (
    game.lineup.find((player) => player.playerId === playerId)?.name ?? "Runner"
  );
}

function syncBatterIndexToLineup(game: GameDomainDocument): void {
  const lastBatterIndex = game.state.lastBatterId
    ? game.lineup.findIndex(
        (player) => player.playerId === game.state.lastBatterId,
      )
    : -1;
  game.state.batterIndex =
    lastBatterIndex < 0 ? 0 : (lastBatterIndex + 1) % game.lineup.length;
}

function mercyRuleWinnerAfterHalfInning(
  game: GameDomainDocument,
  wasBottom: boolean,
): GameSide | null {
  const state = game.state;
  if (state.inning < MERCY_RULE_FIRST_INNING) return null;
  const ourRuns = totalRuns(state.ourRunsByInning);
  const opponentRuns = totalRuns(state.opponentRunsByInning);
  if (Math.abs(ourRuns - opponentRuns) < MERCY_RULE_RUN_DIFFERENTIAL) {
    return null;
  }
  const winner = ourRuns > opponentRuns ? "us" : "them";
  const homeSide = game.isHome ? "us" : "them";
  return wasBottom || winner === homeSide ? winner : null;
}

function homeTeamRunDifferential(game: GameDomainDocument): number {
  const ourRuns = totalRuns(game.state.ourRunsByInning);
  const opponentRuns = totalRuns(game.state.opponentRunsByInning);
  return game.isHome ? ourRuns - opponentRuns : opponentRuns - ourRuns;
}

function applyHomeTeamMercyRuleIfCrossed(
  game: GameDomainDocument,
  previousDifferential: number,
): boolean {
  const state = game.state;
  const homeSide = game.isHome ? "us" : "them";
  if (
    state.inning < MERCY_RULE_FIRST_INNING ||
    state.batting !== homeSide ||
    previousDifferential >= MERCY_RULE_RUN_DIFFERENTIAL ||
    homeTeamRunDifferential(game) < MERCY_RULE_RUN_DIFFERENTIAL
  ) {
    return false;
  }
  state.final = true;
  state.mercyRuleWinner = homeSide;
  return true;
}

function applyHalfInningTransition(game: GameDomainDocument): void {
  const state = game.state;
  state.bases = [null, null, null];
  state.outs = 0;
  const wasBottom = (state.batting === "us") === game.isHome;
  const mercyRuleWinner = mercyRuleWinnerAfterHalfInning(game, wasBottom);
  state.batting = state.batting === "us" ? "them" : "us";
  if (mercyRuleWinner) {
    state.final = true;
    state.mercyRuleWinner = mercyRuleWinner;
  }
  if (wasBottom) {
    if (
      !mercyRuleWinner &&
      state.inning >= NUM_REGULATION_INNINGS &&
      totalRuns(state.ourRunsByInning) !== totalRuns(state.opponentRunsByInning)
    ) {
      state.final = true;
    }
    state.inning += 1;
    ensureInning(state.ourRunsByInning, state.inning - 1);
    ensureInning(state.opponentRunsByInning, state.inning - 1);
    game.openInning = false;
  }
}

function requireLiveUsBatting(game: GameDomainDocument): void {
  if (game.state.final) {
    throw new GameActionError("failed-precondition", "Game is final");
  }
  if (game.state.batting !== "us") {
    throw new GameActionError("failed-precondition", "Not our half-inning");
  }
}

function requireValidBaseIndex(
  baseIndex: unknown,
): asserts baseIndex is BaseIndex {
  if (
    !Number.isInteger(baseIndex) ||
    typeof baseIndex !== "number" ||
    baseIndex < 0 ||
    baseIndex > 2
  ) {
    throw new GameActionError(
      "invalid-argument",
      `Invalid base index: ${String(baseIndex)}`,
    );
  }
}

function resultFor(value: unknown): {
  key: GameResultKey;
  result: PlateAppearanceResult;
} | null {
  if (typeof value !== "string" || !(value in RESULTS)) return null;
  const key = value as GameResultKey;
  return { key, result: RESULTS[key] };
}

function recordPlateAppearance(
  game: GameDomainDocument,
  context: Required<GameTeamContext>,
  payload: unknown,
): DomainActionResult {
  const state = game.state;
  requireLiveUsBatting(game);
  const values = recordValue(payload);
  const batterId = values.batterId;
  const selectedResult = resultFor(values.resultKey);
  const resultKey = selectedResult?.key;
  const result = selectedResult?.result;
  const runnerDecisions = recordValue(values.runnerDecisions);
  const rbi = values.rbi ?? 0;
  if (!result || !resultKey) {
    throw new GameActionError(
      "invalid-argument",
      `Unknown result: ${String(values.resultKey)}`,
    );
  }
  if (!game.lineup.length) {
    throw new GameActionError("failed-precondition", "Lineup is empty");
  }
  const batter = game.lineup[state.batterIndex % game.lineup.length]!;
  if (batterId !== batter.playerId) {
    throw new GameActionError(
      "failed-precondition",
      "Not this batter's plate appearance",
      { expectedBatterId: batter.playerId },
    );
  }
  if (typeof rbi !== "number" || !Number.isInteger(rbi) || rbi < 0) {
    throw new GameActionError(
      "invalid-argument",
      "rbi must be a non-negative integer",
    );
  }

  for (const baseKey of Object.keys(runnerDecisions)) {
    const baseIndex = Number(baseKey);
    if (!Number.isInteger(baseIndex) || baseIndex < 0 || baseIndex > 2) {
      throw new GameActionError(
        "invalid-argument",
        `Invalid base in decisions: ${baseKey}`,
      );
    }
    if (!state.bases[baseIndex as BaseIndex]) {
      throw new GameActionError(
        "invalid-argument",
        `No runner on base ${baseIndex}`,
      );
    }
  }
  if (resultKey === "FC") {
    if (!state.bases.some(Boolean)) {
      throw new GameActionError(
        "invalid-argument",
        "Fielder's choice requires a runner on base",
      );
    }
    const fielderChoiceOuts = Object.values(runnerDecisions).filter(
      (destination) => destination === "X",
    ).length;
    if (fielderChoiceOuts !== 1) {
      throw new GameActionError(
        "invalid-argument",
        "Fielder's choice requires exactly one runner out",
      );
    }
  }
  const batterDestination =
    !result.out && resultKey !== "HR"
      ? (((result.bases ?? 1) - 1) as BaseIndex)
      : null;
  const numericDestinations: number[] = [];
  for (const baseIndex of [0, 1, 2] as const) {
    if (!state.bases[baseIndex]) continue;
    const destination = runnerDecisions[baseIndex] ?? baseIndex;
    if (destination === "H" || destination === "X") continue;
    const numericDestination = Number(destination);
    if (
      !Number.isInteger(numericDestination) ||
      numericDestination < baseIndex ||
      numericDestination > 2
    ) {
      throw new GameActionError(
        "invalid-argument",
        `Invalid destination ${String(destination)} for runner on base ${baseIndex}`,
      );
    }
    numericDestinations.push(numericDestination);
  }
  if (new Set(numericDestinations).size !== numericDestinations.length) {
    throw new GameActionError(
      "invalid-argument",
      "Runner destinations collide",
    );
  }
  if (
    batterDestination !== null &&
    numericDestinations.includes(batterDestination)
  ) {
    throw new GameActionError(
      "invalid-argument",
      "Runner destination collides with the batter",
    );
  }
  const previousHomeTeamDifferential = homeTeamRunDifferential(game);

  if (result.out) {
    if (!result.sf) bump(state, batter.playerId, "AB");
    if (resultKey === "K") bump(state, batter.playerId, "K");
    if (result.sf) bump(state, batter.playerId, "SF");
  } else {
    if (!result.walk) bump(state, batter.playerId, "AB");
    if (result.hit) {
      bump(state, batter.playerId, "H");
      if (HIT_RESULT_KEYS.has(resultKey as keyof BaseballStats)) {
        bump(state, batter.playerId, resultKey as keyof BaseballStats);
      }
    }
    if (result.walk) bump(state, batter.playerId, "BB");
  }

  const cap = runsCapFor(game, true);
  const inningIndex = state.inning - 1;
  ensureInning(state.ourRunsByInning, inningIndex);
  let capRemaining =
    cap === Infinity
      ? Infinity
      : Math.max(0, cap - (state.ourRunsByInning[inningIndex] ?? 0));
  let runs = 0;
  let truncated = 0;
  let outsAdded = result.out ? 1 : 0;
  const scored: string[] = [];
  const newBases: GameState["bases"] = [null, null, null];

  for (const baseIndex of [2, 1, 0] as const) {
    const playerId = state.bases[baseIndex];
    if (!playerId) continue;
    const destination = runnerDecisions[baseIndex] ?? baseIndex;
    if (destination === "H") {
      if (capRemaining > 0) {
        scored.push(playerId);
        runs += 1;
        capRemaining -= 1;
      } else {
        truncated += 1;
      }
    } else if (destination === "X") {
      outsAdded += 1;
    } else {
      newBases[Number(destination) as BaseIndex] = playerId;
    }
  }

  if (!result.out) {
    if (resultKey === "HR") {
      if (capRemaining > 0) {
        scored.push(batter.playerId);
        runs += 1;
        capRemaining -= 1;
      } else {
        truncated += 1;
      }
    } else if (batterDestination !== null) {
      newBases[batterDestination] = batter.playerId;
    }
  }

  const cappedRbi = Math.min(rbi, runs);
  for (const playerId of scored) bump(state, playerId, "R");
  if (cappedRbi > 0) bump(state, batter.playerId, "RBI", cappedRbi);

  state.ourRunsByInning[inningIndex] =
    (state.ourRunsByInning[inningIndex] ?? 0) + runs;
  const outsOnBases = Object.values(runnerDecisions).filter(
    (destination) => destination === "X",
  ).length;
  const summary =
    `${batter.name}: ${result.label}` +
    `${runs ? ` (+${runs} R)` : ""}` +
    `${cappedRbi ? ` ${cappedRbi} RBI` : ""}` +
    `${outsOnBases ? ` (${outsOnBases} out on bases)` : ""}` +
    `${truncated ? ` [${truncated} run${truncated > 1 ? "s" : ""} wiped by cap]` : ""}`;

  state.lastBatterId = batter.playerId;
  state.batterIndex = (state.batterIndex + 1) % game.lineup.length;
  state.bases = newBases;

  const newOuts = state.outs + outsAdded;
  applyHomeTeamMercyRuleIfCrossed(game, previousHomeTeamDifferential);
  if (
    ((state.ourRunsByInning[inningIndex] ?? 0) >= cap && newOuts < 3) ||
    newOuts >= 3
  ) {
    applyHalfInningTransition(game);
  } else {
    state.outs = newOuts;
  }

  const teamEffects: GameTeamEffects = {};
  const auditPayload: Record<string, unknown> = {
    batterId: batter.playerId,
    resultKey,
    runs,
    rbi: cappedRbi,
    outsOnBases,
    truncated,
  };
  if (resultKey === "K") {
    teamEffects.jerseyHolderPlayerId = batter.playerId;
    auditPayload.fromPlayerId = context.jerseyHolderPlayerId;
    auditPayload.toPlayerId = batter.playerId;
  }
  return {
    teamEffects,
    audit: {
      type: "recordPlateAppearance",
      summary,
      payload: auditPayload,
    },
  };
}

function adjustRunner(
  game: GameDomainDocument,
  payload: unknown,
): DomainActionResult {
  const state = game.state;
  requireLiveUsBatting(game);
  const values = recordValue(payload);
  const baseIndex = values.baseIndex;
  const destination = values.destination;
  const rbiToLastBatter = values.rbiToLastBatter ?? 0;
  requireValidBaseIndex(baseIndex);
  const playerId = state.bases[baseIndex];
  if (!playerId) {
    throw new GameActionError("failed-precondition", "No runner on that base");
  }
  if (
    typeof rbiToLastBatter !== "number" ||
    !Number.isInteger(rbiToLastBatter) ||
    rbiToLastBatter < 0 ||
    rbiToLastBatter > 1
  ) {
    throw new GameActionError(
      "invalid-argument",
      "rbiToLastBatter must be 0 or 1",
    );
  }
  const name = playerName(game, playerId);
  let summary: string;
  if (destination === "H") {
    if (baseIndex !== 2) {
      throw new GameActionError(
        "invalid-argument",
        "Runner can only score from third",
      );
    }
    const cap = runsCapFor(game, true);
    const inningIndex = state.inning - 1;
    ensureInning(state.ourRunsByInning, inningIndex);
    if ((state.ourRunsByInning[inningIndex] ?? 0) >= cap) {
      throw new GameActionError(
        "failed-precondition",
        `Run cap (${cap}) reached — run doesn't count`,
        { reason: "run-cap-reached", cap },
      );
    }
    const previousHomeTeamDifferential = homeTeamRunDifferential(game);
    state.bases[2] = null;
    state.ourRunsByInning[inningIndex] =
      (state.ourRunsByInning[inningIndex] ?? 0) + 1;
    bump(state, playerId, "R");
    if (rbiToLastBatter && state.lastBatterId) {
      bump(state, state.lastBatterId, "RBI");
    }
    applyHomeTeamMercyRuleIfCrossed(game, previousHomeTeamDifferential);
    summary = `${name} scores (advanced home)`;
  } else {
    const numericDestination = Number(destination);
    if (
      !Number.isInteger(numericDestination) ||
      numericDestination !== baseIndex + 1
    ) {
      throw new GameActionError(
        "invalid-argument",
        "Runner can only advance one base",
      );
    }
    const destinationIndex = numericDestination as BaseIndex;
    if (state.bases[destinationIndex]) {
      throw new GameActionError("failed-precondition", "Next base is occupied");
    }
    state.bases[destinationIndex] = playerId;
    state.bases[baseIndex] = null;
    summary = `${name} advances to ${destinationIndex === 1 ? "2nd" : "3rd"}`;
  }
  return {
    teamEffects: {},
    audit: {
      type: "adjustRunner",
      summary,
      payload: { playerId, baseIndex, destination, rbiToLastBatter },
    },
  };
}

function removeRunner(
  game: GameDomainDocument,
  payload: unknown,
): DomainActionResult {
  const state = game.state;
  requireLiveUsBatting(game);
  const baseIndex = recordValue(payload).baseIndex;
  requireValidBaseIndex(baseIndex);
  const playerId = state.bases[baseIndex];
  if (!playerId) {
    throw new GameActionError("failed-precondition", "No runner on that base");
  }
  const name = playerName(game, playerId);
  state.bases[baseIndex] = null;
  const newOuts = state.outs + 1;
  if (newOuts >= 3) applyHalfInningTransition(game);
  else state.outs = newOuts;
  return {
    teamEffects: {},
    audit: {
      type: "removeRunner",
      summary: `${name} out on the bases`,
      payload: { playerId, baseIndex },
    },
  };
}

function addOpponentRun(
  game: GameDomainDocument,
  payload: unknown,
): DomainActionResult {
  const state = game.state;
  if (state.final) {
    throw new GameActionError("failed-precondition", "Game is final");
  }
  const delta = recordValue(payload).delta;
  if (delta !== 1 && delta !== -1) {
    throw new GameActionError("invalid-argument", "delta must be 1 or -1");
  }
  const cap = runsCapFor(game, false);
  const inningIndex = state.inning - 1;
  ensureInning(state.opponentRunsByInning, inningIndex);
  const currentRuns = state.opponentRunsByInning[inningIndex] ?? 0;
  const previousHomeTeamDifferential = homeTeamRunDifferential(game);
  if (delta > 0 && currentRuns >= cap) {
    throw new GameActionError(
      "failed-precondition",
      `Run cap (${cap}) reached`,
      { reason: "run-cap-reached", cap },
    );
  }
  if (delta < 0 && currentRuns <= 0) {
    throw new GameActionError(
      "failed-precondition",
      "No opponent runs to remove this inning",
    );
  }
  state.opponentRunsByInning[inningIndex] = Math.max(
    0,
    Math.min(cap === Infinity ? 999 : cap, currentRuns + delta),
  );
  applyHomeTeamMercyRuleIfCrossed(game, previousHomeTeamDifferential);
  return {
    teamEffects: {},
    audit: {
      type: "addOpponentRun",
      summary: `Opponent run ${delta > 0 ? "+1" : "-1"}`,
      payload: { delta },
    },
  };
}

function endHalfInning(game: GameDomainDocument): DomainActionResult {
  if (game.state.final) {
    throw new GameActionError("failed-precondition", "Game is final");
  }
  applyHalfInningTransition(game);
  return {
    teamEffects: {},
    audit: {
      type: "endHalfInning",
      summary: "Half-inning ended",
      payload: {},
    },
  };
}

function updateLiveSetup(
  game: GameDomainDocument,
  payload: unknown,
): DomainActionResult {
  const state = game.state;
  const values = recordValue(payload);
  if (values.opponentName !== undefined) {
    if (typeof values.opponentName !== "string") {
      throw new GameActionError(
        "invalid-argument",
        "opponentName must be a string",
      );
    }
    game.opponentName = values.opponentName;
  }
  if (values.openInning !== undefined) {
    if (typeof values.openInning !== "boolean") {
      throw new GameActionError(
        "invalid-argument",
        "openInning must be a boolean",
      );
    }
    if (values.openInning && state.inning >= NUM_REGULATION_INNINGS) {
      throw new GameActionError(
        "failed-precondition",
        "9th inning and beyond are always open",
      );
    }
    game.openInning = values.openInning;
  }
  if (values.final !== undefined) {
    if (typeof values.final !== "boolean") {
      throw new GameActionError("invalid-argument", "final must be a boolean");
    }
    state.final = values.final;
    if (!values.final) delete state.mercyRuleWinner;
  }
  if (values.lineupPlayerIds !== undefined) {
    if (!Array.isArray(values.lineupPlayerIds)) {
      throw new GameActionError(
        "invalid-argument",
        "lineupPlayerIds must be an array",
      );
    }
    const currentIds = game.lineup.map((lineupEntry) => lineupEntry.playerId);
    const sort = (ids: readonly string[]) => [...ids].sort();
    if (
      values.lineupPlayerIds.some((playerId) => typeof playerId !== "string") ||
      values.lineupPlayerIds.length !== currentIds.length ||
      sort(values.lineupPlayerIds as string[]).join() !==
        sort(currentIds).join()
    ) {
      throw new GameActionError(
        "invalid-argument",
        "lineupPlayerIds must be a permutation of the current lineup",
      );
    }
    const byId = new Map(
      game.lineup.map((lineupEntry) => [lineupEntry.playerId, lineupEntry]),
    );
    game.lineup = (values.lineupPlayerIds as string[]).map((playerId) =>
      byId.get(playerId)!,
    );
    syncBatterIndexToLineup(game);
  }
  if (values.defense !== undefined) {
    if (!Array.isArray(values.defense)) {
      throw new GameActionError("invalid-argument", "defense must be an array");
    }
    game.defense = clone(values.defense);
  }
  return {
    teamEffects: {},
    audit: {
      type: "updateLiveSetup",
      summary: "Live setup updated",
      payload: clone(values),
    },
  };
}

function finalizeGame(
  game: GameDomainDocument,
  payload: unknown,
): DomainActionResult {
  if (game.state.final) {
    throw new GameActionError("failed-precondition", "Game is already final");
  }
  const forfeitWinner = recordValue(payload).forfeitWinner;
  if (
    forfeitWinner !== undefined &&
    !FORFEIT_WINNERS.has(forfeitWinner as GameSide)
  ) {
    throw new GameActionError(
      "invalid-argument",
      'forfeitWinner must be "us" or "them"',
    );
  }
  game.state.final = true;
  const summary = forfeitWinner
    ? `Game finalized — forfeit, ${forfeitWinner === "us" ? "win" : "loss"} recorded`
    : "Game finalized";
  return {
    teamEffects: {},
    audit: {
      type: "finalizeGame",
      summary,
      payload: forfeitWinner ? { forfeitWinner } : {},
    },
  };
}

function attributeRbi(
  game: GameDomainDocument,
  payload: unknown,
): DomainActionResult {
  const state = game.state;
  if (state.final && state.mercyRuleWinner !== "us") {
    throw new GameActionError("failed-precondition", "Game is final");
  }
  if (state.batting !== "us") {
    throw new GameActionError("failed-precondition", "Not our half-inning");
  }
  const values = recordValue(payload);
  const batterId = values.batterId;
  const amount = values.n;
  if (typeof batterId !== "string" || !batterId) {
    throw new GameActionError("invalid-argument", "batterId is required");
  }
  if (
    typeof amount !== "number" ||
    !Number.isInteger(amount) ||
    amount < 1 ||
    amount > 4
  ) {
    throw new GameActionError(
      "invalid-argument",
      "n must be an integer between 1 and 4",
    );
  }
  const entry = game.lineup.find(
    (lineupEntry) => lineupEntry.playerId === batterId,
  );
  if (!entry || !state.statsByPlayerId[batterId]) {
    throw new GameActionError(
      "failed-precondition",
      "Batter is not in this game's lineup",
      { reason: "unknown-batter", batterId },
    );
  }
  state.statsByPlayerId[batterId].RBI += amount;
  return {
    teamEffects: {},
    audit: {
      type: "attributeRbi",
      summary: `RBI credited to ${entry.name}${amount > 1 ? ` (+${amount})` : ""}`,
      payload: { batterId, n: amount },
    },
  };
}

export function reduceGameAction<Game extends GameDomainDocument>(
  game: Game,
  teamContext: GameTeamContext | null | undefined,
  action: GameAction | null | undefined,
): GameActionResult<Game> {
  if (!action || !isGameActionType(action.type)) {
    if (!action || typeof action.type !== "string") {
      throw new GameActionError("invalid-argument", "action.type is required");
    }
    throw new GameActionError(
      "invalid-argument",
      `Unknown action type: ${action.type}`,
    );
  }
  const context: Required<GameTeamContext> = {
    jerseyHolderPlayerId: teamContext?.jerseyHolderPlayerId ?? null,
  };
  const nextGame = clone(game);
  let result: DomainActionResult;
  switch (action.type) {
    case "recordPlateAppearance":
      result = recordPlateAppearance(nextGame, context, action.payload);
      break;
    case "adjustRunner":
      result = adjustRunner(nextGame, action.payload);
      break;
    case "removeRunner":
      result = removeRunner(nextGame, action.payload);
      break;
    case "addOpponentRun":
      result = addOpponentRun(nextGame, action.payload);
      break;
    case "endHalfInning":
      result = endHalfInning(nextGame);
      break;
    case "updateLiveSetup":
      result = updateLiveSetup(nextGame, action.payload);
      break;
    case "finalizeGame":
      result = finalizeGame(nextGame, action.payload);
      break;
    case "attributeRbi":
      result = attributeRbi(nextGame, action.payload);
      break;
    default:
      throw new GameActionError(
        "invalid-argument",
        `Unknown action type: ${String(action.type)}`,
      );
  }
  const undoable = UNDOABLE.has(action.type);
  const crossedHalfInning =
    undoable &&
    (nextGame.state.inning !== game.state.inning ||
      nextGame.state.batting !== game.state.batting);
  return {
    nextGame,
    teamEffects: result.teamEffects,
    audit: result.audit,
    inversePatch: undoable
      ? {
          state: clone(game.state),
          openInning: Boolean(game.openInning),
          jerseyHolderPlayerId: context.jerseyHolderPlayerId,
          crossedHalfInning,
        }
      : null,
    pushUndo: undoable,
  };
}

export function applyInversePatch<Game extends GameDomainDocument>(
  game: Game,
  inversePatch: unknown,
): { nextGame: Game; teamEffects: Required<GameTeamEffects> } {
  const patch = recordValue(inversePatch);
  if (!patch.state) {
    throw new GameActionError("invalid-argument", "Invalid inverse patch");
  }
  const nextGame = clone(game);
  nextGame.state = clone(patch.state as GameState);
  nextGame.openInning = Boolean(patch.openInning);
  syncBatterIndexToLineup(nextGame);
  return {
    nextGame,
    teamEffects: {
      jerseyHolderPlayerId:
        typeof patch.jerseyHolderPlayerId === "string"
          ? patch.jerseyHolderPlayerId
          : null,
    },
  };
}
